import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { api } from "../../App";
import { toast } from "sonner";
import {
  TrendingUp,
  Users,
  FileText,
  PoundSterling,
  CalendarCheck,
  Truck,
  ClipboardList,
  ArrowRight,
  Clock,
  MapPin,
  Loader2,
  Target,
  Zap,
} from "lucide-react";
import { AlertBanners } from "./dashboard/AlertBanners";

const formatStatus = (s) =>
  (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const KPICard = ({ label, value, icon: Icon, color, subtext }) => (
  <div
    className="bg-white border border-slate-200 p-4 sm:p-5 hover:shadow-md transition-shadow"
    data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="flex items-center justify-between mb-2">
      <div className={`w-9 h-9 ${color} flex items-center justify-center rounded-lg`}>
        <Icon className="w-4.5 h-4.5 text-white" />
      </div>
    </div>
    <div className="text-2xl font-['Outfit'] font-bold text-[#0F172A] tabular-nums">{value}</div>
    <div className="text-sm text-slate-500 leading-tight">{label}</div>
    {subtext && <div className="text-xs text-slate-400 mt-1">{subtext}</div>}
  </div>
);

const PipelineBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-slate-600">{label}</span>
        <span className="font-medium text-[#0F172A] tabular-nums">{count}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.get("/admin/dashboard");
      setData(response.data);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <AdminLayout title="Operations Dashboard">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-10 h-10 animate-spin text-[#FF5A1F]" />
        </div>
      </AdminLayout>
    );
  }

  const kpis = data?.kpis || {};

  /* Only show deposits subtext when non-zero */
  const depositsSubtext =
    kpis.deposits_today_amount > 0 ? `£${kpis.deposits_today_amount.toFixed(2)} collected` : null;

  /* Only show "this week" subtext when it differs from today */
  const leadsSubtext =
    kpis.new_leads_week !== kpis.new_leads_today ? `${kpis.new_leads_week} this week` : null;

  const kpiCards = [
    { label: "New Leads Today", value: kpis.new_leads_today, icon: Zap, color: "bg-blue-600", subtext: leadsSubtext },
    { label: "Total Quotes", value: kpis.total_quotes, icon: FileText, color: "bg-slate-700" },
    { label: "Quotes Processed", value: kpis.quotes_processed, icon: Target, color: "bg-purple-600" },
    { label: "Lead-to-Booking", value: `${kpis.conversion_rate}%`, icon: TrendingUp, color: "bg-green-600" },
    { label: "Deposits Today", value: kpis.deposits_today_count, icon: PoundSterling, color: "bg-amber-500", subtext: depositsSubtext },
    { label: "Confirmed Bookings", value: kpis.confirmed_bookings, icon: CalendarCheck, color: "bg-emerald-600" },
    { label: "Expected Revenue", value: `£${(kpis.revenue_pipeline || 0).toLocaleString()}`, icon: PoundSterling, color: "bg-[#FF5A1F]", subtext: "From confirmed quotes" },
    { label: "Total Customers", value: kpis.total_customers, icon: Users, color: "bg-indigo-500" },
  ];

  const todaySchedule = data?.today_schedule || [];
  const upcomingSurveys = data?.upcoming_surveys || [];
  const duplicateAlerts = data?.duplicate_alerts || [];
  const staleLeads = data?.stale_leads || [];
  const expiringSoon = data?.expiring_soon || [];
  const recentQuotes = data?.recent_quotes || [];

  const getStatusColor = (status) => {
    const colors = {
      new: "bg-blue-100 text-blue-700",
      estimated: "bg-yellow-100 text-yellow-700",
      survey_required: "bg-orange-100 text-orange-700",
      quoted: "bg-purple-100 text-purple-700",
      deposit_paid: "bg-green-100 text-green-700",
      confirmed: "bg-green-100 text-green-700",
      completed: "bg-slate-100 text-slate-700",
      cancelled: "bg-red-100 text-red-700",
      expired: "bg-slate-200 text-slate-500",
      pending: "bg-yellow-100 text-yellow-700",
      requested: "bg-blue-100 text-blue-700",
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  return (
    <AdminLayout title="Operations Dashboard">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-testid="kpi-grid">
        {kpiCards.map((card) => (
          <KPICard key={card.label} {...card} />
        ))}
      </div>

      {/* Alert Banners */}
      <AlertBanners
        staleLeads={staleLeads}
        duplicateAlerts={duplicateAlerts}
        expiringSoon={expiringSoon}
        getStatusColor={getStatusColor}
      />

      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
        {/* Today's Move Schedule */}
        <div className="bg-white border border-slate-200" data-testid="today-schedule">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-[#FF5A1F]" />
              <h2 className="font-['Outfit'] font-bold text-[#0F172A] text-sm sm:text-base">Today's Moves</h2>
            </div>
            <Link to="/admin/bookings" className="text-sm text-[#FF5A1F] flex items-center gap-1 hover:underline">
              All Bookings <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {todaySchedule.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <CalendarCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <div className="text-sm">No moves scheduled for today</div>
              </div>
            ) : (
              todaySchedule.map((move) => (
                <div key={move.quote_id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-[#0F172A] text-sm">{move.customer?.name || "Customer"}</span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(move.status)}`}>
                      {formatStatus(move.status)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{move.from_postcode} → {move.to_postcode}</span>
                  </div>
                  <div className="flex items-center flex-wrap gap-2 mt-1.5 text-xs text-slate-400">
                    <span>{move.bedrooms} bed {move.property_type}</span>
                    {move.booking?.assigned_crew && (
                      <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">Crew: {move.booking.assigned_crew}</span>
                    )}
                    {move.booking?.assigned_van && (
                      <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">Van: {move.booking.assigned_van}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Upcoming Surveys */}
        <div className="bg-white border border-slate-200" data-testid="upcoming-surveys">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-purple-600" />
              <h2 className="font-['Outfit'] font-bold text-[#0F172A] text-sm sm:text-base">Upcoming Surveys</h2>
            </div>
            <Link to="/admin/surveys" className="text-sm text-[#FF5A1F] flex items-center gap-1 hover:underline">
              All Surveys <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {upcomingSurveys.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <div className="text-sm">No surveys in the next 7 days</div>
              </div>
            ) : (
              upcomingSurveys.map((survey) => (
                <div key={survey.survey_id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-[#0F172A] text-sm">{survey.customer?.name || "Customer"}</span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(survey.status)}`}>
                      {formatStatus(survey.status)}
                    </span>
                  </div>
                  <div className="text-sm text-slate-500">
                    <span className="font-medium">{survey.confirmed_date || survey.requested_date}</span>
                    {survey.requested_time && (
                      <><span className="mx-1.5 text-slate-300">|</span><span>{survey.requested_time}</span></>
                    )}
                  </div>
                  {survey.quote && (
                    <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                      <MapPin className="w-3 h-3 shrink-0" />
                      <span>{survey.quote.from_postcode} → {survey.quote.to_postcode}</span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Recent Quotes + Pipeline Summary */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white border border-slate-200" data-testid="recent-quotes">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-['Outfit'] font-bold text-[#0F172A] text-sm sm:text-base">Recent Quotes</h2>
            <Link to="/admin/quotes" className="text-sm text-[#FF5A1F] flex items-center gap-1 hover:underline">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {recentQuotes.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">No quotes yet</div>
            ) : (
              recentQuotes.map((quote) => (
                <div key={quote.quote_id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium truncate">{quote.from_postcode} → {quote.to_postcode}</span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded shrink-0 ml-2 ${getStatusColor(quote.status)}`}>
                      {formatStatus(quote.status)}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500">
                    {quote.bedrooms} bed {quote.property_type} &middot; Move: {quote.move_date}
                  </div>
                  {(quote.customer_price || quote.estimated_price) ? (
                    <div className="text-sm font-medium text-[#FF5A1F] mt-1 tabular-nums">
                      £{(quote.final_price || quote.customer_price || quote.estimated_price || 0).toFixed(2)}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-300 mt-1">Pending estimate</div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pipeline Summary */}
        <div className="bg-white border border-slate-200" data-testid="pipeline-summary">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-['Outfit'] font-bold text-[#0F172A] text-sm sm:text-base">Pipeline Summary</h2>
          </div>
          <div className="p-4 space-y-4">
            <PipelineBar label="Pending Quotes" count={kpis.pending_quotes} total={kpis.total_quotes} color="bg-yellow-500" />
            <PipelineBar label="Quotes Processed" count={kpis.quotes_processed} total={kpis.total_quotes} color="bg-purple-500" />
            <PipelineBar label="Confirmed Bookings" count={kpis.confirmed_bookings} total={kpis.total_quotes || 1} color="bg-green-500" />
            <div className="pt-4 border-t border-slate-100">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-500">Deposits Collected</span>
                <span className="font-bold text-[#0F172A] tabular-nums">£{(kpis.total_revenue || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Expected Revenue</span>
                <span className="font-bold text-[#FF5A1F] tabular-nums">£{(kpis.revenue_pipeline || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
